import { IDayPartDto } from 'generated/graphql-gateway';
import type { IDayPartBoundary, IValidDayPart } from 'state/menu/main-menu/types';

const DAY_START = [
  'sunOpen',
  'monOpen',
  'tueOpen',
  'wedOpen',
  'thrOpen',
  'friOpen',
  'satOpen',
] as const;
const DAY_END = [
  'sunClose',
  'monClose',
  'tueClose',
  'wedClose',
  'thrClose',
  'friClose',
  'satClose',
] as const;

/**
 * Projects One Menu payload schedules for the store-local weekday.
 * Payload IDs remain stable operational keys and are not localized presentation copy.
 */
export function getDayPartsForDate(
  dayParts: ReadonlyArray<IDayPartDto>,
  currentTime: Date
): IValidDayPart[] {
  const dayOfWeek = currentTime.getDay();
  const result: IValidDayPart[] = [];

  for (const dayPart of dayParts) {
    const startTime = dayPart.schedule[DAY_START[dayOfWeek]];
    const endTime = dayPart.schedule[DAY_END[dayOfWeek]];
    if (!startTime || !endTime) {
      continue;
    }

    result.push({
      endTime,
      key: dayPart.id,
      startTime,
    });
  }

  return result;
}

/** Returns active One Menu day parts with their current schedule boundaries. */
export function findActiveDayParts(
  dayParts: ReadonlyArray<IDayPartDto>,
  currentTime: Date
): IDayPartBoundary[] {
  const currDayOfWeek = currentTime.getDay();
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  const result: IDayPartBoundary[] = [];
  for (const dayPart of dayParts) {
    const currentDaySchedule = getDaySchedule(dayPart, currDayOfWeek);
    if (currentDaySchedule) {
      const {
        closeHour,
        closeMinute,
        closeMinutes,
        openHour,
        openMinute,
        openMinutes,
      } = currentDaySchedule;
      const isSameDayRange = openMinutes <= closeMinutes;
      const isCurrentDayActive = isSameDayRange
        ? currentMinutes >= openMinutes && currentMinutes <= closeMinutes
        : currentMinutes >= openMinutes;

      if (isCurrentDayActive) {
        const startTime = withTime(currentTime, openHour, openMinute);
        const endTime = withTime(currentTime, closeHour, closeMinute);
        if (!isSameDayRange) {
          endTime.setDate(endTime.getDate() + 1);
        }

        result.push({
          endTime,
          key: dayPart.id,
          startTime,
        });
        continue;
      }
    }

    const previousDayOfWeek = (currDayOfWeek + 6) % 7;
    const previousDaySchedule = getDaySchedule(dayPart, previousDayOfWeek);
    if (
      previousDaySchedule &&
      previousDaySchedule.openMinutes > previousDaySchedule.closeMinutes &&
      currentMinutes <= previousDaySchedule.closeMinutes
    ) {
      const startTime = withTime(
        currentTime,
        previousDaySchedule.openHour,
        previousDaySchedule.openMinute
      );
      startTime.setDate(startTime.getDate() - 1);
      const endTime = withTime(
        currentTime,
        previousDaySchedule.closeHour,
        previousDaySchedule.closeMinute
      );
      result.push({
        endTime,
        key: dayPart.id,
        startTime,
      });
      continue;
    }
  }

  return result;
}

function withTime(date: Date, hour: number, minute: number): Date {
  const result = new Date(date);
  result.setHours(hour, minute, 0, 0);
  return result;
}

function parseTime(open: string): [number, number] {
  const openHour = parseInt(open.substring(0, 2), 10);
  const openMinute = parseInt(open.substring(3, 5), 10);
  return [openHour, openMinute];
}

interface IDaySchedule {
  closeHour: number;
  closeMinute: number;
  closeMinutes: number;
  openHour: number;
  openMinute: number;
  openMinutes: number;
}

function getDaySchedule(dayPart: IDayPartDto, dayOfWeek: number): IDaySchedule | undefined {
  const open = dayPart.schedule[DAY_START[dayOfWeek]];
  const close = dayPart.schedule[DAY_END[dayOfWeek]];
  if (!open || !close) {
    return;
  }

  const [openHour, openMinute] = parseTime(open);
  const [closeHour, closeMinute] = parseTime(close);
  return {
    closeHour,
    closeMinute,
    closeMinutes: closeHour * 60 + closeMinute,
    openHour,
    openMinute,
    openMinutes: openHour * 60 + openMinute,
  };
}
